import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { useLang } from "../contexts/LangContext";
import { LOCATIONS } from "../data/locations";

// Harta (cu tot cu Leaflet) se incarca intr-un chunk separat, abia cand
// sectiunea se apropie de viewport — homepage-ul ramane usor
const LocationsMap = lazy(() => import("./LocationsMap"));

const AboutSection = () => {
  const { t } = useLang();
  const sectionRef = useRef(null);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShowMap(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="about"
      className="bg-paper pt-10 pb-20 lg:pt-14 lg:pb-28 relative"
      data-testid="about-section"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase tracking-overline text-coral mb-4">
              {t.about.kicker}
            </div>
            <h2 className="font-display font-black tracking-tight text-4xl lg:text-6xl text-burgundy leading-[1.05]">
              {t.about.title}
            </h2>
            <p className="mt-8 text-black/70 font-light leading-relaxed text-base lg:text-lg max-w-xl">
              {t.about.p1}
            </p>
            <p className="mt-4 text-black/70 font-light leading-relaxed text-base lg:text-lg max-w-xl">
              {t.about.p2}
            </p>

            <div className="mt-12 grid grid-cols-3 gap-px bg-[#5a1a14]/15 border border-[#5a1a14]/15">
              <Stat value="1995" label={t.about.stat_year} />
              <Stat value="12" label={t.about.stat_branches} />
              <Stat value="14" label={t.about.stat_currencies} />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-overline text-coral mb-4">
              {t.map.kicker}
            </div>
            {showMap ? (
              <Suspense fallback={<MapSkeleton />}>
                <LocationsMap locations={LOCATIONS} />
              </Suspense>
            ) : (
              <MapSkeleton />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const MapSkeleton = () => (
  <div>
    <div className="flex gap-2 mb-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-8 w-24 bg-[#5a1a14]/10 animate-pulse" />
      ))}
    </div>
    <div className="h-[440px] sm:h-[520px] lg:h-[600px] border border-[#5a1a14]/15 bg-[#efe9e1] animate-pulse" />
  </div>
);

const Stat = ({ value, label }) => (
  <div className="bg-paper p-4 lg:p-8" data-testid={`about-stat-${label}`}>
    <div className="font-display text-burgundy text-2xl lg:text-5xl font-black leading-none">
      {value}
    </div>
    <div className="mt-1 lg:mt-2 text-[9px] lg:text-[10px] uppercase tracking-overline text-black/55">{label}</div>
  </div>
);

export default AboutSection;
